/**
 * All useState and useRef for Bid Analysis template.
 * Other hooks and Full receive this state bag to read/update.
 */
import { useEffect, useRef, useState } from 'react';
import type { BidNote } from 'services/bidAnalysis/bidAnalysisServices';
import type { MarketLandscapeItem } from 'services/bidAnalysis/bidAnalysisServices';
import type { BidAnalysisTemplate } from 'services/bidAnalysis/bidAnalysisServices';
import type { MissingFieldsReport } from '../shared/types';

export interface ContractPriceRankingItem {
  ndc: string;
  contracter_name: string | null;
  labeller_name: string;
  price_type: string;
  price: number;
  current_wac: number | null;
  current_market_share: string | null;
  extra_data: {
    contract_start_date: string | null;
    percentage_of_commercial_market: string | null;
    percentage_of_government_market: string | null;
    nad: string | null;
    nad_expiry: string | null;
  };
  price_rank: number;
}

const EXPANDED_SECTIONS_INITIAL: Record<string, boolean> = {
  projectInfo: true,
  productDetails: true,
  marketLandscape: true,
  currentMarket: true,
  cogsBuildUp: true,
  pricingCalc: true,
  fssTracking: true,
  packSize: true,
  competitive: true,
  contractPrice: true,
  commercialPrice: true,
  tmopCalc: true,
  globalOutput: true,
  wacAnalysis: true,
};

export function useBidAnalysisState() {
  const [templateData, setTemplateData] = useState<BidAnalysisTemplate | null>(null);
  const [bidOwner, setBidOwner] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('project-details');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activePackSizeTab, setActivePackSizeTab] = useState(0);
  const [activeYearTab, setActiveYearTab] = useState(0);
  const [selectedConcept, setSelectedConcept] = useState<string | null>(null);
  const [expandedRows, setExpandedRows] = useState<any>({});
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
  const [isBackNavigation, setIsBackNavigation] = useState(false);
  const [isNavigatingAway, setIsNavigatingAway] = useState(false);

  const [packSizeFormData, setPackSizeFormData] = useState<Record<number, any>>({ 0: {} });
  const packSizeFormDataRef = useRef<Record<number, any>>(packSizeFormData);
  useEffect(() => {
    packSizeFormDataRef.current = packSizeFormData;
  }, [packSizeFormData]);

  const [yearFormData, setYearFormData] = useState<Record<number, Record<number, any>>>({});
  const yearFormDataRef = useRef<Record<number, Record<number, any>>>({});
  useEffect(() => {
    yearFormDataRef.current = yearFormData;
  }, [yearFormData]);

  const copyAllYearsDataRef = useRef<Record<number, Record<number, any>> | null>(null);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>(EXPANDED_SECTIONS_INITIAL);

  const [pricingValidatedChecked, setPricingValidatedChecked] = useState(false);
  const [analysisType, setAnalysisType] = useState('New Launch');
  const [gsmsHndlgType, setGsmsHndlgType] = useState<string>('');
  const gsmsHndlgTypeManuallyChangedRef = useRef<boolean>(false);

  const [formData, setFormData] = useState<any>({});
  const [selectedCompany, setSelectedCompany] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [ndcValidated, setNdcValidated] = useState(false);
  const [isValidatingNdc, setIsValidatingNdc] = useState(false);
  const [isLoadingNdcData, setIsLoadingNdcData] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmittingForReview, setIsSubmittingForReview] = useState(false);
  const [marketLandscapeData, setMarketLandscapeData] = useState<MarketLandscapeItem[]>([]);
  const [wasGsmsProfitManuallySet, setWasGsmsProfitManuallySet] = useState(false);
  const [showChartModal, setShowChartModal] = useState(false);
  const [showMissingFieldsModal, setShowMissingFieldsModal] = useState(false);
  const [missingFieldsReport, setMissingFieldsReport] = useState<MissingFieldsReport | null>(null);
  const [isLoadingMarketTrends, setIsLoadingMarketTrends] = useState(false);
  const [isLoadingMarketShare, setIsLoadingMarketShare] = useState(false);
  const [bidStatus, setBidStatus] = useState<string>('Draft');
  const [selectedStatus, setSelectedStatus] = useState<string>('Draft');

  const [isRequestModificationModalVisible, setIsRequestModificationModalVisible] = useState(false);
  const [isApproveDialogVisible, setIsApproveDialogVisible] = useState(false);

  const [notes, setNotes] = useState<BidNote[]>([]);
  const [isLoadingNotes, setIsLoadingNotes] = useState(false);
  const [notesPanelSection, setNotesPanelSection] = useState<string>('manager_modification');
  const notesPanelRef = useRef<any>(null);

  const [marketShareByVolume, setMarketShareByVolume] = useState<Array<{ vendor: string; share: number }>>([]);
  const [volumeByCot, setVolumeByCot] = useState<Array<{ cot: string; volume: number }>>([]);
  const [isLoadingVolumeByCot, setIsLoadingVolumeByCot] = useState(false);
  const volumeByCotAbortControllerRef = useRef<AbortController | null>(null);

  const [ndcOptions, setNdcOptions] = useState<Array<{
    ndc11: string;
    brand_name: string;
    pack_size: number;
    labeler: string;
    concept_name: string;
  }>>([]);
  const [contractsList, setContractsList] = useState<Array<{ id: number; contract_name: string }>>([]);
  const [handlingTypesList, setHandlingTypesList] = useState<Array<{ id: number; handling_type: string }>>([]);
  const [supplierName, setSupplierName] = useState<string>('');
  const [supplierOptions, setSupplierOptions] = useState<Array<{ label: string; value: string }>>([]);
  const [marketSalesTrendData, setMarketSalesTrendData] = useState<Array<{ month: string; dod_volume: number; va_volume: number; sales: number }>>([]);
  const [filteredMarketSalesTrendData, setFilteredMarketSalesTrendData] = useState<Array<{ month: string; dod_volume: number; va_volume: number; sales: number }>>([]);
  const [chartDateRange, setChartDateRange] = useState<{ startDate: Date | null; endDate: Date | null }>({ startDate: null, endDate: null });
  const [tempDateRange, setTempDateRange] = useState<{ startDate: Date | null; endDate: Date | null }>({ startDate: null, endDate: null });
  const [calendarViewDates, setCalendarViewDates] = useState<{ startViewDate: Date | null; endViewDate: Date | null }>({ startViewDate: null, endViewDate: null });
  const isApplyingFilterRef = useRef(false);

  const marketSalesTrendAbortControllerRef = useRef<AbortController | null>(null);
  const marketShareAbortControllerRef = useRef<AbortController | null>(null);
  const contractPriceRankingAbortControllerRef = useRef<AbortController | null>(null);
  const contractPriceRankingDebounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastContractPriceRankingParamsRef = useRef<{ startDate?: string; endDate?: string; concept?: string; packSize?: number } | null>(null);

  const [contractPriceRanking, setContractPriceRanking] = useState<ContractPriceRankingItem[]>([]);
  const [isLoadingContractPriceRanking, setIsLoadingContractPriceRanking] = useState(false);

  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const hasLoadedInitialData = useRef<boolean>(false);
  const contractsExplicitlyClearedRef = useRef<boolean>(false);
  const ndcNotFoundShownRef = useRef<boolean>(false);

  return {
    templateData,
    setTemplateData,
    bidOwner,
    setBidOwner,
    loading,
    setLoading,
    activeSection,
    setActiveSection,
    sidebarCollapsed,
    setSidebarCollapsed,
    activePackSizeTab,
    setActivePackSizeTab,
    activeYearTab,
    setActiveYearTab,
    selectedConcept,
    setSelectedConcept,
    expandedRows,
    setExpandedRows,
    pendingNavigation,
    setPendingNavigation,
    isBackNavigation,
    setIsBackNavigation,
    isNavigatingAway,
    setIsNavigatingAway,
    packSizeFormData,
    setPackSizeFormData,
    packSizeFormDataRef,
    yearFormData,
    setYearFormData,
    yearFormDataRef,
    copyAllYearsDataRef,
    expandedSections,
    setExpandedSections,
    pricingValidatedChecked,
    setPricingValidatedChecked,
    analysisType,
    setAnalysisType,
    gsmsHndlgType,
    setGsmsHndlgType,
    gsmsHndlgTypeManuallyChangedRef,
    formData,
    setFormData,
    selectedCompany,
    setSelectedCompany,
    validationErrors,
    setValidationErrors,
    ndcValidated,
    setNdcValidated,
    isValidatingNdc,
    setIsValidatingNdc,
    isLoadingNdcData,
    setIsLoadingNdcData,
    isSaving,
    setIsSaving,
    isSubmittingForReview,
    setIsSubmittingForReview,
    marketLandscapeData,
    setMarketLandscapeData,
    wasGsmsProfitManuallySet,
    setWasGsmsProfitManuallySet,
    showChartModal,
    setShowChartModal,
    showMissingFieldsModal,
    setShowMissingFieldsModal,
    missingFieldsReport,
    setMissingFieldsReport,
    isLoadingMarketTrends,
    setIsLoadingMarketTrends,
    isLoadingMarketShare,
    setIsLoadingMarketShare,
    volumeByCot,
    setVolumeByCot,
    isLoadingVolumeByCot,
    setIsLoadingVolumeByCot,
    volumeByCotAbortControllerRef,
    bidStatus,
    setBidStatus,
    selectedStatus,
    setSelectedStatus,
    isRequestModificationModalVisible,
    setIsRequestModificationModalVisible,
    isApproveDialogVisible,
    setIsApproveDialogVisible,
    notes,
    setNotes,
    isLoadingNotes,
    setIsLoadingNotes,
    notesPanelSection,
    setNotesPanelSection,
    notesPanelRef,
    ndcOptions,
    setNdcOptions,
    contractsList,
    setContractsList,
    handlingTypesList,
    setHandlingTypesList,
    supplierName,
    setSupplierName,
    supplierOptions,
    setSupplierOptions,
    marketSalesTrendData,
    setMarketSalesTrendData,
    filteredMarketSalesTrendData,
    setFilteredMarketSalesTrendData,
    marketShareByVolume,
    setMarketShareByVolume,
    chartDateRange,
    setChartDateRange,
    tempDateRange,
    setTempDateRange,
    calendarViewDates,
    setCalendarViewDates,
    isApplyingFilterRef,
    marketSalesTrendAbortControllerRef,
    marketShareAbortControllerRef,
    contractPriceRankingAbortControllerRef,
    contractPriceRankingDebounceTimerRef,
    lastContractPriceRankingParamsRef,
    contractPriceRanking,
    setContractPriceRanking,
    isLoadingContractPriceRanking,
    setIsLoadingContractPriceRanking,
    sectionRefs,
    hasLoadedInitialData,
    contractsExplicitlyClearedRef,
    ndcNotFoundShownRef,
  };
}
